// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";

import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLine from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLine";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import {
    ProductModel,
    ProductPriceDto,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MyListDetailsProductListProductControls from "@store/Library/Widgets/MyListDetails/MyListDetailsProductListLineProductControls";
import MyListDetailsProductListLineProductItem from "@store/Library/Widgets/MyListDetails/MyListDetailsProductListLineProductItem";
import MyListDetailsRestrictedLine from "@store/Library/Widgets/MyListDetails/MyListDetailsRestrictedLine";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cloneDeep from "lodash/cloneDeep";
import React, { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    canDrag?: boolean;
    isEditingSortOrder?: boolean;
    customWishListLineUpdate?: (wishListLine: WishListLineModel) => void;
    quantityUpdateOnSuccess?: () => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
    showSpecificationModal?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
    updateWishListLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    lineInnerContainer?: GridContainerProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    wrapper?: InjectableCss;
}
export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    lineInnerContainer: {
        gap: 20,
        offsetCss: css`
            display: flex;
            flex-wrap: nowrap !important;

            ${breakpointValues(
                ["flex-start !important", null, null, "center !important", "center !important"],
                ["align-items"],
            )}
        `,
    },

    wrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = productListLineStyles;

type NullablePricing = ProductPriceDto | null | undefined;

export const getExtendedPrice = (pricing: NullablePricing, quantityOrdered: number) => {
    const hasBreakPricing = pricing?.actualBreakPrices && pricing?.actualBreakPrices.length > 0;
    const priceFactor = Number(pricing?.additionalResults?.priceFactor) || 0;
    let unitPrice = pricing?.actualPrice || 0;
    if (hasBreakPricing) {
        const breakPrice = pricing?.actualBreakPrices?.find((product, index) => {
            const nextProduct = pricing?.actualBreakPrices?.[index + 1];
            return quantityOrdered >= product.breakQty && (!nextProduct || quantityOrdered < nextProduct.breakQty);
        });

        unitPrice = breakPrice?.breakPrice || unitPrice;
    }
    let extendedPrice = quantityOrdered * unitPrice;
    if (priceFactor > 1) {
        extendedPrice = extendedPrice / priceFactor;
    }
    return Number(extendedPrice.toFixed(2));
};

const MyListsDetailsBunzlProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    isQuantityUpdated,
    onDeleteClick,
    updateWishListLineQuantities,
    updateUnitOfMeasure,
    updateQuantity,
    canDrag,
    isEditingSortOrder = false,
    customWishListLineUpdate,
    quantityUpdateOnSuccess,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    showImageBadges,
    showTextBadges,
    onViewReplacementProduct,
    showSpecificationModal,
}) => {
    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true, onSuccess: quantityUpdateOnSuccess });
    };
    const product = useMemo(() => mapWishListLineToProduct(wishListLine), [wishListLine]);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const canEditWishList = wishList.allowEdit || !wishList.isSharedList;

    const wishListLineIsRestricted = isWishListLineRestricted(wishListLine, productInfo);
    const wishListLineIsDiscontinued = isWishListLineDiscontinued(wishListLine, productInfo);
    const productContext: ProductContextModel = useMemo(
        () => ({
            product,
            productInfo,
            onQtyOrderedChanged: qtyOrdered => {
                updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
            },
            onUnitOfMeasureChanged: unitOfMeasure => {
                updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
            },
        }),
        [product, productInfo, updateQuantity, updateUnitOfMeasure, wishListLine.id],
    );

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }
    if (wishListLineIsRestricted || wishListLineIsDiscontinued) {
        return (
            <ProductContext.Provider value={productContext}>
                <MyListDetailsRestrictedLine
                    canEditWishList={canEditWishList}
                    wishListLineIsDiscontinued={wishListLineIsDiscontinued}
                    wishListLine={wishListLine}
                    wishListLineIsRestricted={wishListLineIsRestricted}
                    onDeleteClick={onDeleteClick}
                />
            </ProductContext.Provider>
        );
    }

    const extendedPrice = getExtendedPrice(productInfo.pricing, productInfo.qtyOrdered);
    return (
        <ProductContext.Provider value={productContext} {...styles.wrapper}>
            <GridContainer
                {...styles.lineInnerContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <MyListDetailsProductListLineProductItem
                    isEditingSortOrder={isEditingSortOrder}
                    canDrag={canDrag}
                    isSelected={isSelected}
                    wishListLine={wishListLine}
                    customWishListLineUpdate={customWishListLineUpdate}
                    wishList={wishList}
                    canEditWishList={canEditWishList}
                    extendedPrice={extendedPrice}
                    isQuantityUpdated={isQuantityUpdated || false}
                    onUpdateQuantityClick={onUpdateQuantityClick}
                    maxImageBadges={maximumNumberOfImageBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                    showImageBadges={showImageBadges}
                    showTextBadges={showTextBadges}
                    showSpecificationModal={showSpecificationModal ?? false}
                    onDeleteClick={onDeleteClick}
                />
                <MyListDetailsProductListProductControls
                    canEditWishList={canEditWishList}
                    onUpdateQuantityClick={onUpdateQuantityClick}
                    extendedPrice={extendedPrice}
                    wishListLine={wishListLine}
                    onDeleteClick={onDeleteClick}
                />
            </GridContainer>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsBunzlProductListLine);
